(() => {
  'use strict';

  const rootSelector = '[data-pb-story-experience]';
  const mountedRoots = new WeakMap();

  const prefersReducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasFineHover = () => window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const normalizeState = (value) => value === 'back' ? 'back' : 'front';

  const setFace = (pair, state, immediate = false) => {
    const nextState = normalizeState(state);

    pair.querySelectorAll('[data-pb-story-face]').forEach((face) => {
      face.hidden = face.dataset.pbStoryFace !== nextState;
      face.setAttribute('aria-hidden', face.hidden ? 'true' : 'false');
      if (immediate) {
        face.style.setProperty('transition-duration', '0ms');
      } else {
        face.style.removeProperty('transition-duration');
      }
    });

    pair.dataset.pbState = nextState;
    pair.querySelectorAll('[data-pb-story-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-pressed', nextState === 'back' ? 'true' : 'false');
    });
  };

  const setStaticFaces = (pair) => {
    pair.dataset.pbState = 'static';
    pair.dataset.pbPreviewState = 'static';
    pair.dataset.pbUserSelected = 'false';
    pair.querySelectorAll('[data-pb-story-face]').forEach((face) => {
      face.hidden = false;
      face.setAttribute('aria-hidden', 'false');
      face.style.removeProperty('transition-duration');
    });
    pair.querySelectorAll('[data-pb-story-toggle]').forEach((toggle) => {
      toggle.hidden = true;
      toggle.disabled = true;
      toggle.tabIndex = -1;
      toggle.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-pressed', 'false');
    });
  };

  const setInteractiveControls = (pair) => {
    pair.querySelectorAll('[data-pb-story-toggle]').forEach((toggle) => {
      toggle.hidden = false;
      toggle.disabled = false;
      toggle.tabIndex = 0;
      toggle.removeAttribute('aria-hidden');
    });
  };

  const mount = (root) => {
    const previous = mountedRoots.get(root);
    if (previous) previous.abort();

    const controller = new AbortController();
    mountedRoots.set(root, controller);
    const { signal } = controller;
    const revealMode = root.dataset.pbRevealMode || 'static';
    const staticRoot = prefersReducedMotion() || revealMode === 'static';
    const pairs = Array.from(root.querySelectorAll('[data-pb-story-pair]'));

    root.dataset.pbStoryStatic = staticRoot ? 'true' : 'false';
    if (staticRoot) {
      root.dataset.pbMounted = 'false';
      pairs.forEach(setStaticFaces);
      signal.addEventListener('abort', () => {
        root.dataset.pbMounted = 'false';
        root.removeAttribute('data-pb-story-static');
      }, { once: true });
      return;
    }

    root.dataset.pbMounted = 'true';
    pairs.forEach((pair) => {
      const initialState = normalizeState(pair.dataset.pbState);
      pair.dataset.pbUserSelected = 'false';
      pair.dataset.pbPreviewState = initialState;
      pair.dataset.pbPointerType = '';
      setInteractiveControls(pair);
      setFace(pair, initialState);

      const toggle = pair.querySelector('[data-pb-story-toggle]');
      if (!toggle) return;

      let pointerStartX = 0;
      let pointerStartY = 0;
      let pointerTracking = false;
      let pointerDragged = false;

      const activate = () => {
        const selected = pair.dataset.pbUserSelected === 'true';
        if (!selected) {
          // The first activation commits the preview to the back face. It is
          // intentionally not an inversion, so hover/focus cannot steal a tap.
          pair.dataset.pbUserSelected = 'true';
          pair.dataset.pbPreviewState = 'back';
          setFace(pair, 'back');
          return;
        }

        const nextState = pair.dataset.pbState === 'back' ? 'front' : 'back';
        pair.dataset.pbPreviewState = nextState;
        setFace(pair, nextState);
      };

      toggle.addEventListener('pointerdown', (event) => {
        pair.dataset.pbPointerType = event.pointerType || '';
      }, { signal });

      // Native button activation supplies Enter/Space semantics without a
      // second keyboard owner that could double-toggle the click event. A
      // meaningful horizontal drag belongs to the mobile character rail and
      // must not become a tap toggle when the browser dispatches click.
      toggle.addEventListener('click', (event) => {
        if (toggle.dataset.pbStoryDragged === 'true') {
          delete toggle.dataset.pbStoryDragged;
          event.preventDefault();
          return;
        }
        activate();
      }, { signal });

      toggle.addEventListener('pointerdown', (event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
        pointerTracking = true;
        pointerDragged = false;
      }, { signal });
      toggle.addEventListener('pointermove', (event) => {
        if (!pointerTracking) return;
        const deltaX = Math.abs(event.clientX - pointerStartX);
        const deltaY = Math.abs(event.clientY - pointerStartY);
        if (deltaX >= 8 && deltaX > deltaY) pointerDragged = true;
      }, { signal });
      toggle.addEventListener('pointerup', () => {
        if (pointerDragged) toggle.dataset.pbStoryDragged = 'true';
        pointerTracking = false;
      }, { signal });
      toggle.addEventListener('pointercancel', () => {
        if (pointerDragged) toggle.dataset.pbStoryDragged = 'true';
        pointerTracking = false;
      }, { signal });

      pair.addEventListener('pointerenter', (event) => {
        if (!hasFineHover() || (event.pointerType && event.pointerType !== 'mouse')) return;
        if (pair.dataset.pbUserSelected === 'true') return;
        pair.dataset.pbPreviewState = 'back';
        setFace(pair, 'back');
      }, { signal });

      pair.addEventListener('pointerleave', (event) => {
        if (!hasFineHover() || (event.pointerType && event.pointerType !== 'mouse')) return;
        if (pair.dataset.pbUserSelected === 'true') return;
        pair.dataset.pbPreviewState = 'front';
        setFace(pair, 'front');
      }, { signal });

      toggle.addEventListener('focus', () => {
        if (pair.dataset.pbUserSelected === 'true') return;
        pair.dataset.pbPreviewState = 'back';
        setFace(pair, 'back');
      }, { signal });

      toggle.addEventListener('blur', () => {
        if (pair.dataset.pbUserSelected === 'true') return;
        pair.dataset.pbPreviewState = 'front';
        setFace(pair, 'front');
      }, { signal });
    });

    if (revealMode === 'scroll' && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const pair = entry.target;
          if (pair.dataset.pbUserSelected === 'true') return;
          pair.dataset.pbPreviewState = 'back';
          setFace(pair, 'back');
        });
      }, { threshold: 0.35 });
      pairs.forEach((pair) => observer.observe(pair));
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    }

    const sectionId = root.dataset.pbSectionId;
    const isThisSection = (event) => {
      const eventSectionId = event.detail && event.detail.sectionId;
      return (eventSectionId && eventSectionId === sectionId) || event.target === root || root.contains(event.target);
    };
    document.addEventListener('shopify:section:select', (event) => {
      if (!isThisSection(event)) return;
      pairs.forEach((pair) => {
        if (pair.dataset.pbUserSelected === 'true') return;
        pair.dataset.pbPreviewState = 'back';
        setFace(pair, 'back');
      });
    }, { signal });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (!isThisSection(event)) return;
      pairs.forEach((pair) => {
        if (pair.dataset.pbUserSelected === 'true') return;
        pair.dataset.pbPreviewState = 'front';
        setFace(pair, 'front');
      });
    }, { signal });

    signal.addEventListener('abort', () => {
      root.dataset.pbMounted = 'false';
      root.removeAttribute('data-pb-story-static');
    }, { once: true });
  };

  const unmount = (root) => {
    const controller = mountedRoots.get(root);
    if (!controller) return;
    controller.abort();
    mountedRoots.delete(root);
  };

  const rootsIn = (scope) => {
    if (scope && scope.matches && scope.matches(rootSelector)) return [scope];
    return Array.from((scope || document).querySelectorAll(rootSelector));
  };

  const mountAll = (scope) => rootsIn(scope).forEach(mount);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountAll(document), { once: true });
  } else {
    mountAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => mountAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => rootsIn(event.target).forEach(unmount));
})();
